/* Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { When } from "react-if";
import { FlexContainer, SVGIcon, Text, theme, Tooltip } from "@nordcloud/gnui";
import { isNotNil } from "~/tools";
import {
  ColoredBar,
  Indicator,
  PercentageLabel,
  Section,
  SectionLabel,
  SectionsContainer,
} from "./styles";

const SECTIONS_PRESET = [
  { from: 0, to: 50, label: "PRE-CRAWL" },
  { from: 50, to: 70, label: "CRAWL", caption: "Getting the basics in place" },
  {
    from: 70,
    to: 90,
    label: "WALK",
    caption: "Established practice, needs maturing",
  },
  { from: 90, to: 100, label: "RUN", caption: "Mature and evolving practice" },
] as const;

type Props = { value: number; loading: boolean };

export function KpiMaturityLevel({ value, loading }: Props) {
  return (
    <>
      <Text weight="medium">FinOps Maturity</Text>
      <ColoredBar>
        <SectionsContainer>
          {SECTIONS_PRESET.map((preset, index) => (
            <MaturitySection
              key={index}
              value={value}
              loading={loading}
              {...preset}
            />
          ))}
        </SectionsContainer>
      </ColoredBar>
    </>
  );
}

type SectionProps = {
  from: number;
  to: number;
  value: number;
  label?: string;
  caption?: string;
  loading: boolean;
};

function MaturitySection({
  from,
  to,
  value,
  label,
  caption,
  loading,
}: SectionProps) {
  const sectionInterval = to - from;
  const marginLeft = ((value - from) * 100) / sectionInterval;

  const showIndicator =
    !loading && (from === 0 ? value >= from : value > from) && value <= to;

  return (
    <Section>
      <When condition={showIndicator}>
        <Indicator marginLeft={marginLeft} />
      </When>
      <PercentageLabel lastSection={to === 100}>{to}%</PercentageLabel>
      <SectionLabel>
        <FlexContainer columnGap={theme.spacing.spacing01}>
          <Text size="sm" tag="span" color={theme.color.text.text02}>
            {label}
          </Text>
          <When condition={isNotNil(caption)}>
            <Tooltip bottom caption={caption} minWidth="10rem">
              <SVGIcon
                name="info"
                size="sm"
                css={{
                  position: "absolute",
                  top: "1px",
                  fill: theme.color.text.text02,
                }}
              />
            </Tooltip>
          </When>
        </FlexContainer>
      </SectionLabel>
    </Section>
  );
}
